import { z } from "zod";

export const analyticsRangeSchema = z.object({
	range_days: z.coerce.number().int().positive().max(365).default(30),
});
export type AnalyticsRange = z.infer<typeof analyticsRangeSchema>;

export interface AnalyticsDayBucket {
	/** YYYY-MM-DD in clinic-local time. */
	date: string;
	total: number;
	completed: number;
	no_show: number;
}

export interface AnalyticsStatusCount {
	status: string;
	count: number;
}

export interface AnalyticsSourceCount {
	source: string;
	count: number;
}

export interface AnalyticsTopService {
	service_id: string | null;
	name: { ar: string; en?: string } | null;
	count: number;
	/** Sum of duration_minutes for these bookings — useful for "time on Botox" type questions. */
	total_minutes: number;
}

export interface AnalyticsResponse {
	range_days: number;
	range_start: string;
	range_end: string;
	total_bookings: number;
	by_day: AnalyticsDayBucket[];
	by_status: AnalyticsStatusCount[];
	by_source: AnalyticsSourceCount[];
	top_services: AnalyticsTopService[];
	/** Booked → no_show / (total scheduled and reached) */
	no_show_rate: number;
	/** Booked → confirmed before the visit / (total scheduled and reached) */
	confirmation_rate: number;
	/** Total / range_days, rounded. */
	avg_per_day: number;
}

// ===== Diagnosis (ICD-10) report =====

export interface DiagnosisReportRow {
	code: string;
	ar: string;
	en: string | null;
	/** Number of visits in the window carrying this code. */
	count: number;
}

export interface DiagnosisReportResponse {
	range_days: number;
	range_start: string;
	range_end: string;
	/** Visits with an occurred_at in the window. */
	total_visits: number;
	/** Visits that carry at least one ICD-10 code. */
	coded_visits: number;
	/** Sum of all code occurrences (a visit can carry several). */
	total_code_entries: number;
	/** Top codes by frequency, descending. */
	rows: DiagnosisReportRow[];
}

// ===== Practice intelligence (owner business view — deterministic) =====

export interface RevenueDayBucket {
	/** YYYY-MM-DD. */
	date: string;
	/** Paid revenue that day (numeric). */
	total: number;
}

export interface DoctorProductivityRow {
	member_id: string | null;
	name: string;
	visits: number;
}

/** A doctor ranked by the paid-invoice revenue attributed to their visits. */
export interface DoctorRevenueRow {
	member_id: string | null;
	name: string;
	/** Paid revenue attributed to this doctor (via the invoice's visit). */
	revenue: number;
	/** Number of paid invoices attributed to this doctor. */
	invoices: number;
}

/** A patient ranked by their total paid spend in the range. */
export interface TopSpenderRow {
	patient_id: string;
	name: string;
	/** Total paid across this patient's invoices in the range. */
	total_spent: number;
	/** Number of paid invoices in the range. */
	invoices: number;
}

/** A catalog service ranked by the amount billed against it in the range. */
export interface ServiceRevenueRow {
	service_id: string | null;
	name: { ar: string; en?: string } | null;
	/** Total BILLED for this service (sum of invoice-item totals) in the range. */
	revenue: number;
	/** Number of line items billed for this service. */
	count: number;
}

export interface PracticeIntelligenceResponse {
	range_days: number;
	/** Paid-invoice revenue per day + total over the range. */
	revenue_by_day: RevenueDayBucket[];
	revenue_total: number;
	/** Visits per doctor in the range (productivity), top first. */
	doctors: DoctorProductivityRow[];
	/** Revenue per doctor (paid invoices via their visits), top first. */
	top_doctors_revenue: DoctorRevenueRow[];
	/** Patients by total paid spend in the range, top first. */
	top_spenders: TopSpenderRow[];
	/** Amount billed per catalog service in the range, top first (accrual/billed,
	 *  not cash — items don't carry payment allocation). */
	revenue_by_service: ServiceRevenueRow[];
	/** Patient retention in the range. */
	patients_seen: number;
	patients_new: number;
	patients_returning: number;
}
